import { useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { formatDate } from '@shared/dates.js';
import type { CaptureRow, RowEditPatch } from '@shared/types.js';

type EditingCell = { rowId: string; field: keyof RowEditPatch } | null;

export function Grid() {
  const {
    rows, settings, showVoided, filter,
    editRow, deleteRow, addRow, moveRow, toast,
  } = useStore((s) => ({
    rows: s.rows,
    settings: s.settings,
    showVoided: s.showVoided,
    filter: s.filter,
    editRow: s.editRow,
    deleteRow: s.deleteRow,
    addRow: s.addRow,
    moveRow: s.moveRow,
    toast: s.toast,
  }));
  const [editing, setEditing] = useState<EditingCell>(null);
  const [draft, setDraft] = useState<string>('');
  const [showAdd, setShowAdd] = useState(false);

  const visible = useMemo(() => {
    let r = rows;
    if (!showVoided) r = r.filter((x) => x.status !== 'void');
    if (filter !== 'all') {
      if (filter === 'warning') {
        r = r.filter((x) => x.validationWarnings.length > 0);
      } else {
        r = r.filter((x) => x.status === filter);
      }
    }
    return r;
  }, [rows, showVoided, filter]);

  const dateFmt = settings?.exportDateFormat ?? 'DD/MM/YYYY';

  function startEdit(row: CaptureRow, field: keyof RowEditPatch) {
    setEditing({ rowId: row.id, field });
    const v = (row as unknown as Record<string, unknown>)[field];
    setDraft(v === null || v === undefined ? '' : String(v));
  }

  async function commitEdit() {
    if (!editing) return;
    const { rowId, field } = editing;
    let value: unknown = draft;
    if (field === 'units' || field === 'decimalTime') {
      const n = Number(draft);
      if (!Number.isFinite(n)) {
        setEditing(null);
        return;
      }
      value = n;
    }
    if (draft === '') value = null;
    await editRow(rowId, { [field]: value as never });
    setEditing(null);
  }

  async function onDeleteRow(r: CaptureRow) {
    const ok = window.confirm(
      `Delete row ${r.rowNumber}?\n\n"${r.description}" (${r.units} unit${r.units === 1 ? '' : 's'})\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    try {
      await deleteRow(r.id);
      toast('success', `Row ${r.rowNumber} deleted`);
    } catch (err) {
      toast('error', `Could not delete: ${(err as Error).message}`);
    }
  }

  return (
    <div className="grid-wrap">
      <table className="grid">
        <thead>
          <tr>
            <th>#</th>
            <th>Date</th>
            <th>Fee earner</th>
            <th>Description</th>
            <th>Units</th>
            <th>Time</th>
            <th>Section</th>
            <th>Reference</th>
            <th>Status</th>
            <th>Warnings</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && (
            <tr><td colSpan={11} className="grid-empty">No rows yet — type a time entry below or click "Add row".</td></tr>
          )}
          {visible.map((r, i) => (
            <tr key={r.id} className={`row row--${r.status} ${r.validationWarnings.length ? 'row--warn' : ''}`}>
              <td className="row-num">{r.rowNumber}</td>
              {(['date','feeEarner','description','units','decimalTime','section','reference'] as const).map((f) => {
                const isEd = editing && editing.rowId === r.id && editing.field === f;
                const display = (() => {
                  if (f === 'date') return formatDate(r.date, dateFmt);
                  const v = (r as unknown as Record<string, unknown>)[f];
                  return v === null || v === undefined ? '' : String(v);
                })();
                return (
                  <td key={f} onDoubleClick={() => startEdit(r, f)} className={`cell cell--${f}`}>
                    {isEd ? (
                      <input
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={() => void commitEdit()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void commitEdit();
                          if (e.key === 'Escape') setEditing(null);
                        }}
                      />
                    ) : (
                      display || <span className="cell-empty">—</span>
                    )}
                  </td>
                );
              })}
              <td className={`status status--${r.status}`}>{r.status}</td>
              <td className="warnings">
                {r.validationWarnings.length === 0 ? '' : r.validationWarnings.join('; ')}
              </td>
              <td className="row-actions">
                <button
                  className="row-action-btn"
                  title="Move up"
                  disabled={i === 0}
                  onClick={() => void moveRow(r.id, 'up')}
                >▲</button>
                <button
                  className="row-action-btn"
                  title="Move down"
                  disabled={i === visible.length - 1}
                  onClick={() => void moveRow(r.id, 'down')}
                >▼</button>
                <button
                  className="row-action-btn row-action-btn--danger"
                  title="Delete row"
                  onClick={() => void onDeleteRow(r)}
                >🗑</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid-help">
        Double-click a cell to edit. Use ▲▼ to reorder, 🗑 to delete. Manually edited rows are flagged.{' '}
        <button className="grid-add-btn" onClick={() => setShowAdd(true)}>+ Add row</button>
      </div>
      {showAdd && (
        <AddRowForm
          onCancel={() => setShowAdd(false)}
          onSubmit={async (draftRow) => {
            try {
              await addRow(draftRow);
              setShowAdd(false);
            } catch (err) {
              toast('error', `Could not add: ${(err as Error).message}`);
            }
          }}
        />
      )}
    </div>
  );
}

function AddRowForm({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (d: {
    description: string;
    units: number;
    date: string | null;
    feeEarner: string | null;
    section: string | null;
    reference: string | null;
  }) => Promise<void>;
}) {
  const cur = useStore((s) => s.current);
  const [description, setDescription] = useState('');
  const [units, setUnits] = useState('1');
  const [date, setDate] = useState(cur?.currentDate ?? '');
  const [feeEarner, setFeeEarner] = useState(cur?.currentFeeEarner ?? '');
  const [section, setSection] = useState(cur?.currentSection ?? '');
  const [reference, setReference] = useState(cur?.currentReference ?? '');

  async function submit() {
    const n = Number(units);
    if (!description.trim()) return;
    if (!Number.isFinite(n) || n <= 0) return;
    await onSubmit({
      description: description.trim(),
      units: n,
      date: date.trim() || null,
      feeEarner: feeEarner.trim() || null,
      section: section.trim() || null,
      reference: reference.trim() || null,
    });
  }

  return (
    <div className="add-row-form">
      <h3>Add a row manually</h3>
      <div className="add-row-grid">
        <label>Date <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="YYYY-MM-DD" /></label>
        <label>Fee earner <input value={feeEarner} onChange={(e) => setFeeEarner(e.target.value)} /></label>
        <label className="add-row-desc">Description <input value={description} onChange={(e) => setDescription(e.target.value)} autoFocus /></label>
        <label>Units <input type="number" min="0" step="1" value={units} onChange={(e) => setUnits(e.target.value)} /></label>
        <label>Section <input value={section} onChange={(e) => setSection(e.target.value)} /></label>
        <label>Reference <input value={reference} onChange={(e) => setReference(e.target.value)} /></label>
      </div>
      <div className="add-row-actions">
        <button onClick={onCancel}>Cancel</button>
        <button
          onClick={() => void submit()}
          disabled={!description.trim() || !Number.isFinite(Number(units)) || Number(units) <= 0}
        >
          Add row
        </button>
      </div>
    </div>
  );
}
